import React, { useEffect, useRef, useState } from "react";
import { fxStyle } from "@/lib/mediaFx";
import { useNavigate } from "react-router-dom";
import { Play, Film, Eye, Share2, Heart, TrendingUp, Check } from "lucide-react";
import { resolveMediaUrl } from "@/components/FileUpload";
import { isHlsSrc, attachHls, prefersNativeHls } from "@/lib/videoSource";
import { compactNumber } from "@/lib/formatNumber";
import { useApp } from "@/context/AppContext";
import { sharePostCard } from "@/lib/nativeShare";
import { postShareLocked } from "@/lib/privacy";
import { api } from "@/lib/api";
import { SoundControl } from "@/components/SoundControl";
import { onPosterError } from "@/lib/neutralPoster";
import { peekReelHandoff, clearReelHandoff } from "@/lib/reelHandoff";
import { warmReelTouch } from "@/lib/reelsWarmup";

// Loop only the first few seconds as a lightweight muted teaser (IG/TikTok-style).
const PREVIEW_SECONDS = 4;

const fmtDur = (s) => {
  s = Math.max(0, Math.floor(Number(s) || 0));
  if (!s) return "";
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const reelPoster = (post) => resolveMediaUrl(post.poster_url || "");

// Reels badge (top-left) + views (bottom-left) + duration (bottom-right).
const Overlays = ({ post, duration, small }) => (
  <>
    <span
      className={`absolute top-2 left-2 z-[4] inline-flex items-center gap-1 rounded-full font-bold text-white ${small ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[11px]"}`}
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
    >
      <Film size={small ? 10 : 12} /> {!small && "Reels"}
    </span>
    {duration ? (
      <span
        className={`absolute bottom-2 right-2 z-[4] rounded-md font-semibold text-white ${small ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[11px]"}`}
        style={{ background: "rgba(0,0,0,0.6)" }}
        data-testid={`reel-duration-${post.id}`}
      >
        {fmtDur(duration)}
      </span>
    ) : null}
    {typeof post.views_count === "number" && post.views_count > 0 ? (
      <span
        className={`absolute bottom-2 left-2 z-[4] inline-flex items-center gap-1 rounded-md font-semibold text-white ${small ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[11px]"}`}
        style={{ background: "rgba(0,0,0,0.6)" }}
        data-testid={`reel-views-${post.id}`}
      >
        <Eye size={small ? 10 : 12} /> {compactNumber(post.views_count)}
      </span>
    ) : null}
  </>
);

// Full-width feed card: tappable → opens the Reels player, with a muted
// auto-preview (first few seconds looping) once the card scrolls into view.
export const ReelFeedPreview = ({ post }) => {
  const nav = useNavigate();
  const { t, user } = useApp();
  const shareLocked = postShareLocked(post, user); // profil privé : partage réservé à l'auteur
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const soundOnRef = useRef(false);
  // Lot 722 — rediffusions courtes devenues reels : la durée connue du direct (replay_duration) sert tant que la vidéo n'a pas chargé.
  const [dur, setDur] = useState(post.video_duration || post.replay_duration || 0);
  // Back from the Reels page: resume the muted teaser at the second the member left
  // (plays on to the end, then the regular first-seconds loop takes over again).
  const resumeAtRef = useRef(0);
  const teaserLoopRef = useRef(true);
  const lastTRef = useRef(0);
  // Poster introuvable (404) → fond de secours : sans ça l'<img> cassée fait 0 px de haut et la carte s'effondre (texte seul).
  const [posterBroken, setPosterBroken] = useState(false);
  const poster = posterBroken ? "" : reelPoster(post);
  // SAME source priority as the Reels player (HLS first): the manifest + first segments
  // the teaser fetches land in the browser HTTP cache, so opening the Reels page replays
  // them instantly instead of starting a brand-new download of a different file.
  const src = resolveMediaUrl(post.hls_url || post.video_url || "");
  const hls = isHlsSrc(src);
  // Background music (chosen in the composer) carries the sound over the reel — the
  // video stays muted unless the author kept its own sound. Same rule as feed posts.
  const hasMusic = !!post.music_url;
  const keepMediaSound = !!post.music_keep_media_sound;
  const musicVolume = typeof post.music_volume === "number" ? post.music_volume : 1;
  const musicStart = post.music_start || 0;
  // Auto-play the muted teaser as soon as the card scrolls into view — on BOTH desktop
  // and mobile (like Facebook / Instagram feeds). Tapping the card opens the Reels player.
  const shouldPlay = inView;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([e]) => setInView(!!e && e.isIntersecting && e.intersectionRatio >= 0.5),
      { threshold: [0, 0.5, 1] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !src) return;
    let cleanup = () => {};
    if (shouldPlay) {
      v.muted = hasMusic && !keepMediaSound ? true : !soundOnRef.current;
      const resumeAt = peekReelHandoff(post.id);
      resumeAtRef.current = resumeAt;
      if (resumeAt > 0) teaserLoopRef.current = false;
      const onReady = () => { setPlaying(true); if (resumeAt > 0) clearReelHandoff(post.id); };
      if (hls) {
        // hls.js starts loading straight at the handoff position (no frame-0 flash);
        // native HLS (iOS) seeks in onMeta instead.
        cleanup = attachHls(v, src, { autoPlay: () => true, onReady, hlsConfig: resumeAt > 0 ? { startPosition: resumeAt } : undefined });
      } else {
        if (!v.src) v.src = src;
        v.play().then(onReady).catch(() => {});
      }
    } else {
      try { v.pause(); v.currentTime = 0; } catch { /* noop */ }
      teaserLoopRef.current = true;
      setPlaying(false);
    }
    return () => { try { cleanup(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPlay, src, hls]);

  // Background music synced to the teaser video's play/pause (plays only once the
  // member taps the sound button — feed teasers stay muted by default).
  useEffect(() => {
    const v = videoRef.current;
    const a = audioRef.current;
    if (!v || !a || !hasMusic) return undefined;
    a.src = resolveMediaUrl(post.music_url);
    a.loop = !musicStart;
    a.volume = musicVolume;
    a.muted = !soundOnRef.current;
    const startAt = musicStart || 0;
    const onPlay = () => { if (!soundOnRef.current) return; try { a.currentTime = startAt; } catch { /* noop */ } a.play().catch(() => {}); };
    const onPause = () => { try { a.pause(); } catch { /* noop */ } };
    const onEnded = () => { if (startAt) { try { a.currentTime = startAt; a.play().catch(() => {}); } catch { /* noop */ } } };
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    if (!v.paused && soundOnRef.current) onPlay();
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
      try { a.pause(); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMusic, post.music_url, musicVolume, musicStart]);

  const onMeta = (e) => {
    const v = e.currentTarget;
    const d = v.duration;
    if (isFinite(d) && d > 0 && !post.video_duration) setDur(d);
    // Native HLS / MP4 paths: seek to the handoff position once metadata is known
    // (the hls.js path already started there via startPosition).
    const at = resumeAtRef.current;
    const nativePath = !hls || prefersNativeHls(v);
    if (at > 0 && nativePath && Math.abs(v.currentTime - at) > 1) { try { v.currentTime = at; } catch { /* noop */ } }
  };
  const onTime = (e) => {
    const v = e.currentTarget;
    const t = v.currentTime;
    if (!teaserLoopRef.current) {
      // Resumed from the Reels page: play on to the end; once <video loop> wraps back
      // to the start, the regular first-seconds teaser loop takes over again.
      if (t < lastTRef.current - 1) teaserLoopRef.current = true;
      lastTRef.current = t;
      return;
    }
    if (t >= PREVIEW_SECONDS) { try { v.currentTime = 0; } catch { /* noop */ } }
  };
  // The reel object travels in the router state so the Reels page can mount the player
  // at once (no skeleton, no API round-trip) — restarting from 0 with sound.
  const openReel = () => nav(`/reels?start=${post.id}`, { state: { back: true, reel: post } });
  const toggleSound = () => {
    const v = videoRef.current;
    const a = audioRef.current;
    const ns = !soundOnRef.current;
    soundOnRef.current = ns;
    setSoundOn(ns);
    if (hasMusic) {
      // Music carries the sound: keep the video muted (unless author kept its sound),
      // and play/pause the background track instead.
      if (v && keepMediaSound) v.muted = !ns;
      if (a) {
        a.muted = !ns;
        if (ns) { try { a.currentTime = musicStart || 0; } catch { /* noop */ } a.play().catch(() => {}); }
        else { try { a.pause(); } catch { /* noop */ } }
      }
    } else if (v) {
      v.muted = !ns;
      if (ns) v.play().catch(() => {});
    }
  };
  const doShare = async (e) => {
    e.stopPropagation();
    const ok = await sharePostCard({
      postId: post.id,
      title: t("reels.share_title", "Yalola Reel"),
      text: post.content || t("reels.share_text", "Regarde ce reel sur Yalola 🙏"),
      copiedMessage: t("common.link_copied", "Lien copié"),
    });
    if (ok) api.post(`/reels/${post.id}/share`).catch(() => {}); // feuille annulée → pas de partage compté
  };

  return (
    <div
      ref={wrapRef}
      role="button"
      tabIndex={0}
      onClick={openReel}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openReel(); } }}
      className="mt-3 relative block w-full overflow-hidden rounded-2xl bg-black group cursor-pointer"
      style={{ maxHeight: 460 }}
      data-testid={`post-reel-${post.id}`}
    >
      {poster ? (
        <>
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: `url(${poster})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(18px)", transform: "scale(1.1)" }} aria-hidden />
          <img
            src={poster}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setPosterBroken(true)}
            className="relative z-[1] mx-auto max-h-[460px] w-auto max-w-full object-contain transition-opacity duration-300"
            style={{ opacity: playing ? 0 : 1, ...(fxStyle(post.video_fx) || {}) }}
            data-testid={`post-reel-poster-${post.id}`}
          />
        </>
      ) : (
        <div className="relative z-[1] w-full" style={{ height: 320, background: "radial-gradient(circle at 50% 32%, #2a2a33 0%, #17171c 60%, #0b0b0d 100%)" }} data-testid={`post-reel-fallback-${post.id}`} />
      )}

      <video
        ref={videoRef}
        muted
        playsInline
        loop
        preload="none"
        poster={poster || undefined}
        onLoadedMetadata={onMeta}
        onTimeUpdate={onTime}
        className="absolute inset-0 z-[2] m-auto max-h-[460px] h-full w-auto max-w-full object-contain transition-opacity duration-300"
        style={{ opacity: playing ? 1 : 0, ...(fxStyle(post.video_fx) || {}) }}
        aria-hidden
      />
      {hasMusic && <audio ref={audioRef} className="hidden" data-testid={`reel-preview-music-${post.id}`} />}

      <div className="absolute inset-0 z-[3] flex items-center justify-center pointer-events-none transition-opacity duration-300" style={{ opacity: playing ? 0 : 1 }}>
        <span className="h-16 w-16 grid place-items-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-transform group-hover:scale-105">
          <Play size={30} fill="white" />
        </span>
      </div>

      <div className="absolute top-2 right-2 z-[5] flex items-center gap-2">
        <SoundControl
          muted={!soundOn}
          onToggle={toggleSound}
          hasAudio={hasMusic ? true : post.has_audio}
          variant="compact"
          noAudioLabel={t("video.no_sound", "Sans son")}
          muteLabel={t("reels.sound_off", "Couper le son")}
          hintLabel={t("reels.sound_on", "Activer le son")}
          testId={`reel-sound-${post.id}`}
        />
        {!shareLocked && (
          <button
            type="button"
            onClick={doShare}
            aria-label={t("post.share_action", "Partager")}
            className="grid place-items-center h-9 w-9 rounded-full text-white transition-transform active:scale-90 hover:scale-105"
            style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)" }}
            data-testid={`reel-share-${post.id}`}
          >
            <Share2 size={16} />
          </button>
        )}
      </div>

      <Overlays post={post} duration={dur} />
    </div>
  );
};

// Compact 9:16 grid thumbnail (profile Reels tab) → opens the Reels player.
// When `showStats` is set (owner viewing their own profile), a small stats row
// (views / likes / shares) is shown under the thumbnail.
export const ReelThumb = ({ post, showStats }) => {
  const nav = useNavigate();
  const { t } = useApp();
  const poster = reelPoster(post);
  const seen = !!post.is_viewed;
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => nav(`/reels?start=${post.id}`, { state: { back: true, reel: post } })}
        onPointerDown={() => warmReelTouch(post)}
        onPointerEnter={() => warmReelTouch(post)}
        className="relative block w-full overflow-hidden rounded-lg bg-black aspect-[9/16] group"
        data-testid={`profile-reel-${post.id}`}
        data-seen={seen ? "1" : "0"}
      >
        {poster ? (
          <img src={poster} alt="" loading="lazy" decoding="async" onError={onPosterError} className={`absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${seen ? "opacity-70" : ""}`} />
        ) : (
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 32%, #2a2a33 0%, #17171c 60%, #0b0b0d 100%)" }} />
        )}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Play size={26} fill="white" className="text-white drop-shadow" />
        </div>
        <Overlays post={post} duration={post.video_duration} small />
        {/* Repère « Vu » (reel_views côté serveur, synchronisé entre appareils) : les nouveautés ressortent d'un coup d'œil. */}
        {seen && (
          <span className="absolute top-2 right-2 z-[4] inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ background: "rgba(16,185,129,0.85)", backdropFilter: "blur(4px)" }} data-testid={`reel-seen-${post.id}`}>
            <Check size={10} strokeWidth={3} /> {t("reels.seen", "Vu")}
          </span>
        )}
      </button>
      {showStats && (
        <div className="flex items-center justify-between px-0.5 text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid={`reel-stats-${post.id}`}>
          <span className="inline-flex items-center gap-0.5"><Eye size={12} /> {compactNumber(post.views_count || 0)}</span>
          <span className="inline-flex items-center gap-0.5"><Heart size={12} /> {compactNumber(post.reaction_total || 0)}</span>
          <span className="inline-flex items-center gap-0.5"><Share2 size={12} /> {compactNumber(post.shares_count || 0)}</span>
        </div>
      )}
    </div>
  );
};

// Horizontal "popular this week" banner (used at the top of the profile Reels tab).
export const TrendingReels = ({ reels, title }) => {
  if (!reels || reels.length === 0) return null;
  return (
    <div className="mb-4" data-testid="trending-reels">
      <div className="flex items-center gap-1.5 mb-2 font-bold font-display">
        <TrendingUp size={16} style={{ color: "var(--yalola-primary, #e11d48)" }} />
        <span>{title}</span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
        {reels.map((p) => (
          <div key={p.id} className="shrink-0 w-24 sm:w-28">
            <ReelThumb post={p} />
          </div>
        ))}
      </div>
    </div>
  );
};
